/**
 * Checklist Tab — Applicant Checklist template (plain text).
 * Master–detail layout; backend wraps payload as `{ applicant_checkbox: { checkbox_data } }`.
 */

import { useState, useEffect, useRef } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import type { SubmitErrorHandler, Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { checklistFormSchema, type ChecklistFormValues } from '../../types/schemas'
import { saveChecklistTemplate } from '../../services/underwritingConfigService'
import type { ChecklistTabProps } from '../../types'
import { showError } from '@/lib/utils/notifications'
import { CHECKLIST_TEMPLATE_FORM_ID } from '../../constants/formIds'
import { ChecklistTemplateMasterDetail } from '../ChecklistTemplateMasterDetail'
import { firstNestedErrorMessage } from '../../utils/formErrors'
import { normalizeTemplateKey } from '../../utils/templateKeys'
import { indexForNewSection, nextSelectedIndexAfterRemoveSection } from '../../utils/selectionUtils'
import { ModuleScreenLoading } from '@/components/ui/ModuleScreenLoading'
import { primaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'

export function ChecklistTab({ template, isLoading, onSuccess, onError }: ChecklistTabProps) {
  const [submitLoading, setSubmitLoading] = useState(false)
  const [sectionModalIndex, setSectionModalIndex] = useState<number | null>(null)
  const pendingAppendFieldIdRef = useRef<string | null>(null)
  const capturePendingAppendIdRef = useRef(false)

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ChecklistFormValues>({
    // `isLock` uses z.preprocess (input type `unknown`), so cast the resolver to the
    // output form type — same pattern as EmailCheckboxTemplateForm.
    resolver: zodResolver(checklistFormSchema) as Resolver<ChecklistFormValues>,
    mode: 'onChange',
    defaultValues: { checkbox_data: [] },
  })

  const { fields, append, remove } = useFieldArray({ control, name: 'checkbox_data' })

  useEffect(() => {
    if (template?.checkbox_data) {
      reset({
        checkbox_data: template.checkbox_data.map((section) => ({
          key: normalizeTemplateKey(section.key),
          label: section.label ?? '',
          child: (section.child ?? []).map((child) => ({
            key: normalizeTemplateKey(child.key),
            label: child.label ?? '',
            description: child.description ?? '',
            hasInput: Boolean(child.hasInput),
            isLock: Number(child.isLock) === 1 ? 1 : 0,
          })),
        })),
      })
    }
  }, [template, reset])

  useEffect(() => {
    if (fields.length === 0) {
      setSectionModalIndex(null)
      return
    }
    setSectionModalIndex((prev) => {
      if (prev === null) return null
      if (prev >= fields.length) return fields.length - 1
      return prev
    })
  }, [fields.length])

  useEffect(() => {
    if (!capturePendingAppendIdRef.current || sectionModalIndex === null) return
    const row = fields[sectionModalIndex]
    if (row) {
      pendingAppendFieldIdRef.current = row.id
      capturePendingAppendIdRef.current = false
    }
  }, [fields, sectionModalIndex])

  const handleDismissSectionModal = () => {
    const pid = pendingAppendFieldIdRef.current
    if (pid !== null && sectionModalIndex !== null) {
      const at = fields[sectionModalIndex]
      if (at?.id === pid) {
        const removeIndex = fields.findIndex((f) => f.id === pid)
        if (removeIndex !== -1) {
          remove(removeIndex)
        }
      }
    }
    pendingAppendFieldIdRef.current = null
    capturePendingAppendIdRef.current = false
    setSectionModalIndex(null)
  }

  const onSubmit = async (data: ChecklistFormValues) => {
    try {
      setSubmitLoading(true)
      await saveChecklistTemplate(data)
      pendingAppendFieldIdRef.current = null
      capturePendingAppendIdRef.current = false
      setSectionModalIndex(null)
      reset(data)
      onSuccess()
    } catch (error) {
      onError(error)
    } finally {
      setSubmitLoading(false)
    }
  }

  const onSubmitInvalid: SubmitErrorHandler<ChecklistFormValues> = (errs) => {
    const msg =
      firstNestedErrorMessage(errs) ??
      'Fix validation errors before saving (required keys cannot be empty).'
    showError(msg)
  }

  const onAddSection = () => {
    capturePendingAppendIdRef.current = true
    const idx = indexForNewSection(fields.length)
    append({ key: '', label: '', child: [] })
    setSectionModalIndex(idx)
  }

  const onRemoveSection = (index: number) => {
    const removed = fields[index]
    if (removed && removed.id === pendingAppendFieldIdRef.current) {
      pendingAppendFieldIdRef.current = null
      capturePendingAppendIdRef.current = false
    }
    const nextLen = fields.length - 1
    remove(index)
    setSectionModalIndex((prev) =>
      nextSelectedIndexAfterRemoveSection(index, prev, nextLen),
    )
  }

  if (isLoading) {
    return <ModuleScreenLoading minHeight="300px" />
  }

  const submitLabel = submitLoading ? 'Submitting...' : 'Submit'
  const listFooterSubmitDisabled = submitLoading

  return (
    <form
      id={CHECKLIST_TEMPLATE_FORM_ID}
      onSubmit={handleSubmit(onSubmit, onSubmitInvalid)}
      className="relative space-y-6 pb-28"
    >
      <ChecklistTemplateMasterDetail
        control={control}
        register={register}
        errors={errors}
        sectionFields={fields}
        onAddSection={onAddSection}
        onRemoveSection={onRemoveSection}
        sectionModalIndex={sectionModalIndex}
        setSectionModalIndex={setSectionModalIndex}
        onDismissSectionModal={handleDismissSectionModal}
        parentFormId={CHECKLIST_TEMPLATE_FORM_ID}
        submitLabel={submitLabel}
        submitLoading={submitLoading}
      />

      <div
        className="sticky bottom-0 z-20 -mx-2 mt-4 flex justify-end gap-2 border-t border-slate-200 bg-white/95 px-3 py-3 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.06)] backdrop-blur supports-[backdrop-filter]:bg-white/90"
        role="toolbar"
        aria-label="Form actions"
      >
        <button
          type="submit"
          disabled={listFooterSubmitDisabled}
          className={primaryEntityActionButtonClass}
        >
          {submitLabel}
        </button>
      </div>
    </form>
  )
}
